/**
 * SipManager.ts
 *
 * iOS LinphoneManager.swift を SIP.js で移植したSIP通話管理クラス。
 * 主な対応:
 *   iOS Factory.createCore + mCore.start()          → new UserAgent(options) + ua.start()
 *   iOS login() / REGISTER                          → new Registerer(ua) + registerer.register()
 *   iOS mCore.inviteAddressWithParams()              → new Inviter(ua, target) + inviter.invite()
 *   iOS call.terminate()                             → session.bye() / inviter.cancel()
 *   iOS CoreDelegate.onCallStateChanged             → session.stateChange listener
 *   iOS CoreDelegate.onAccountRegistrationState     → registerer.stateChange listener
 *   iOS scheduleCallEndWatchdog                     → setTimeout watchdog 2秒
 *   iOS handleCallEndedIfNeeded                     → handleCallEndedIfNeeded (1回のみ通知)
 *   iOS activeCallToken (遅延イベント混入対策)          → activeSessionId: symbol
 *
 * OfficeLink 互換 SDP 調整:
 *   iOS params.addCustomSdpMediaAttribute("ptime") → injectPtimeIntoSdp modifier
 */

import { UserAgent, Registerer, RegistererState, Inviter, SessionState } from "sip.js";
import type { UserAgentOptions, Session } from "sip.js";
import { CallLibraryStatus } from "~/utils/sip.types";
import type { SipCallInfo, SipStatusCallback } from "~/utils/sip.types";

const CALL_END_WATCHDOG_SECONDS = 2;

/**
 * SIP.js を使った通話管理クラス。
 * iOS の LinphoneManager.swift と1対1に近い設計で移植。
 * シングルトンインスタンス (`sipManager`) を末尾でエクスポートする。
 */
export class SipManager {
  // ----- SIP.js オブジェクト -----
  private userAgent: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private activeSession: Session | null = null;

  /**
   * Stale call 対策トークン（iOS の activeCallToken 相当）。
   * 新規通話開始時に Symbol を発行し、旧通話の遅延イベントを無視する。
   */
  private activeSessionId: symbol | null = null;

  // ----- REGISTER 再送制御 (iOS 2025/11/30相当) -----
  private regRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private isTimeoutScheduled = false;
  private isRetryCall = false;
  /** 意図的な stop() 中フラグ（stateChange の誤検知防止） */
  private isStopping = false;
  /**
   * requestDelegate.onReject で取得したエラーコード。
   * Registerer.stateChange の Unregistered と二重処理しないために使う。
   */
  private pendingRegErrorCode: number | null = null;

  // ----- 切断通知の取りこぼし防止 (iOS 2026/03/03相当) -----
  private didNotifyCallDisconnect = false;
  private callEndWatchdogTimer: ReturnType<typeof setTimeout> | null = null;

  // ----- リモート音声再生用 -----
  private remoteAudioElement: HTMLAudioElement | null = null;

  // ----- コールバック / 設定 -----
  private onStatusChange: SipStatusCallback | null = null;
  private callInfo: SipCallInfo | null = null;
  private domain = "";

  // ----- 公開ステータス -----
  isCalling = false;
  isRegistered = false;

  // ============================================================
  // Public API
  // ============================================================

  /**
   * SIPスタックを初期化して登録を開始する。
   * iOS: setupLinphoneLibrary() 相当。
   * @param callInfo - 通話設定 (CallInfoEntity から変換済みの SipCallInfo)
   * @param onStatusChange - ステータス変更コールバック
   */
  setup(callInfo: SipCallInfo, onStatusChange: SipStatusCallback): void {
    this.callInfo = callInfo;
    this.onStatusChange = onStatusChange;
    this.domain = `${callInfo.serverAddress}:${callInfo.serverPort}`;
    this.isStopping = false;
    this.initUserAgent(callInfo);
  }

  /**
   * 指定内線番号に発信する。
   * iOS: call(number:) 相当。
   * @param number - 発信先内線番号
   */
  call(number: string): void {
    if (!this.userAgent || !this.callInfo) return;

    // 新通話開始準備 (iOS resetCallEndGuardsForNewCall 相当)
    this.resetCallEndGuardsForNewCall();

    const targetUri = UserAgent.makeURI(`sip:${number}@${this.domain}`);
    if (!targetUri) return;

    const inviter = new Inviter(this.userAgent, targetUri, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
      sessionDescriptionHandlerModifiers: [createPtimeModifier(this.callInfo.ptimeMs)],
    });

    // この通話セッション固有のトークン (iOS activeCallToken 相当)
    const sessionToken = Symbol("sip-session");
    this.activeSessionId = sessionToken;
    this.activeSession = inviter;

    // 通話ステート変化の購読
    inviter.stateChange.addListener((state: SessionState) => {
      // アクティブセッション以外の遅延イベントを無視
      if (this.activeSessionId !== sessionToken) return;
      this.handleSessionStateChange(state);
    });

    // 発信
    inviter
      .invite({
        requestDelegate: {
          onReject: (response) => {
            const code = (response as unknown as { statusCode?: number }).statusCode ?? 0;
            if (this.activeSessionId !== sessionToken) return;
            this.handleInviteRejection(code);
          },
        },
      })
      .catch(() => {
        if (this.activeSessionId === sessionToken) {
          this.notifyStatus(CallLibraryStatus.callFail);
        }
      });
  }

  /**
   * 現在の通話を切断する。
   * iOS: disconnectCurrentCall() 相当。
   * terminate 失敗でも 2秒後に disconnect を送るウォッチドッグを起動する。
   */
  disconnect(): void {
    if (!this.activeSession) {
      // 通話がない場合でも UI が詰まらないよう終端処理
      this.handleCallEndedIfNeeded();
      return;
    }

    const session = this.activeSession;
    const state = session.state;

    try {
      if (state === SessionState.Establishing) {
        // 発信前のキャンセル
        (session as Inviter).cancel().catch(() => {
          this.handleCallEndedIfNeeded();
        });
      } else if (state === SessionState.Established) {
        // 通話中の切断
        session.bye().catch(() => {
          this.handleCallEndedIfNeeded();
        });
      } else {
        // 既に終了済み
        this.handleCallEndedIfNeeded();
        return;
      }
    } catch {
      // terminate 失敗でも UI を止めない
      this.handleCallEndedIfNeeded();
      return;
    }

    // iOS scheduleCallEndWatchdog 相当: End/Released が来ない場合の保険
    this.scheduleCallEndWatchdog(CALL_END_WATCHDOG_SECONDS);
  }

  /**
   * SIPスタックを完全停止する。
   * iOS: stopLinphoneLibrary() + unregister() + delete() 相当。
   */
  stop(): void {
    this.isStopping = true;
    this.cancelPendingReRegisterSchedule();

    this.registerer?.unregister().catch(() => {});
    this.registerer = null;

    this.userAgent?.stop().catch(() => {});
    this.userAgent = null;

    this.activeSession = null;
    this.activeSessionId = null;
    this.isCalling = false;
    this.isRegistered = false;
    this.cleanupRemoteAudio();
  }

  // ============================================================
  // Private: UserAgent / Registration
  // ============================================================

  /** UserAgent を生成して start → 登録開始 */
  private initUserAgent(callInfo: SipCallInfo): void {
    const uriStr = `sip:${callInfo.name}@${this.domain}`;
    const uri = UserAgent.makeURI(uriStr);
    if (!uri) {
      this.notifyStatus(CallLibraryStatus.cantUse);
      return;
    }

    const options: UserAgentOptions = {
      uri,
      transportOptions: {
        server: `wss://${callInfo.serverAddress}:${callInfo.serverPort}`,
      },
      authorizationUsername: callInfo.name,
      authorizationPassword: callInfo.password,
      displayName: callInfo.displayName,
      logBuiltinEnabled: false,
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: {
          iceServers: this.buildIceServers(),
          iceTransportPolicy: callInfo.turnServerUrl ? "relay" : "all",
        },
      },
    };

    const ua = new UserAgent(options);
    this.userAgent = ua;

    ua.start()
      .then(() => this.startRegistration(callInfo.expiresValue))
      .catch(() => this.notifyStatus(CallLibraryStatus.cantUse));
  }

  /** Registerer を生成して登録ステート変化を購読 → doRegister */
  private startRegistration(expiresValue: number): void {
    if (!this.userAgent) return;

    const registerer = new Registerer(this.userAgent, {
      expires: expiresValue,
    });
    this.registerer = registerer;

    registerer.stateChange.addListener((state: RegistererState) => {
      /**
       * ストールリスナーガード（iOS activeCallToken 相当、登録器版）。
       * React StrictMode 等で setup() が二重実行された場合、古い Registerer の
       * 非同期イベント（Unregistered 等）が遅れて届いても無視する。
       * stop() / scheduleReregisterWithRecreation() が this.registerer = null にした後も
       * 旧インスタンスのイベントが届くため、ここで排除する。
       */
      if (this.registerer !== registerer) return;

      switch (state) {
        case RegistererState.Registered:
          this.isRegistered = true;
          this.pendingRegErrorCode = null;
          this.notifyStatus(CallLibraryStatus.regOk);
          break;

        case RegistererState.Unregistered:
          this.isRegistered = false;
          if (this.isStopping) break;

          if (this.pendingRegErrorCode !== null) {
            // requestDelegate が先にエラーコードを処理済み → 二重処理しない
            this.pendingRegErrorCode = null;
          } else {
            // auto-refresh 失敗または未知のエラー → UA 再生成して Expires 後に再登録
            this.scheduleReregisterWithRecreation(this.callInfo?.expiresValue ?? 3600);
            this.notifyStatus(CallLibraryStatus.regNg);
          }
          break;

        case RegistererState.Terminated:
          this.isRegistered = false;
          if (!this.isStopping) {
            this.notifyStatus(CallLibraryStatus.cantUse);
          }
          break;
      }
    });

    this.doRegister();
  }

  /**
   * REGISTER を送信する。
   * requestDelegate で各エラーコードを受け取り iOS 同等の再登録制御を行う。
   */
  private doRegister(): void {
    this.registerer
      ?.register({
        requestDelegate: {
          onReject: (response) => {
            const code = (response as unknown as { statusCode?: number }).statusCode ?? 0;
            this.pendingRegErrorCode = code;
            this.handleRegistrationError(code);
          },
        },
      })
      .catch(() => {
        this.notifyStatus(CallLibraryStatus.regNg);
      });
  }

  /**
   * REGISTER エラーコードに応じた再登録制御。
   * iOS onAccountRegistrationStateChanged case .Failed 相当。
   */
  private handleRegistrationError(code: number): void {
    this.isRegistered = false;

    if (code === 403 || code === 404) {
      // iOS: registerEnabled=false → Expires 後に Call-ID 再生成で再登録
      this.cancelPendingReRegisterSchedule();
      this.scheduleReregisterWithRecreation(this.callInfo?.expiresValue ?? 3600);
    } else if (code === 408) {
      // iOS: タイムアウト → Expires 後に 1 回だけ再登録（UA 再生成なし）
      this.handleRegistrationTimeout();
      return;
    } else if (code >= 500 && code < 600) {
      // iOS: 5xx → secondsRetryAfterFail 後に Call-ID 再生成で再登録
      this.cancelPendingReRegisterSchedule();
      this.scheduleReregisterWithRecreation(this.callInfo?.secondsRetryAfterFail ?? 60);
    }

    this.notifyStatus(CallLibraryStatus.regNg);
  }

  /**
   * 408 / タイムアウト: Expires 秒後に 1 回だけ再登録（UA 再生成なし）。
   * iOS: scheduleReRegisterAfterExpire 相当。
   */
  private handleRegistrationTimeout(): void {
    if (this.isTimeoutScheduled) return;
    this.isTimeoutScheduled = true;

    const delaySec = this.callInfo?.expiresValue ?? 3600;
    this.regRetryTimer = setTimeout(() => {
      this.regRetryTimer = null;
      this.isTimeoutScheduled = false;
      this.doRegister();
    }, delaySec * 1000);

    this.notifyStatus(CallLibraryStatus.regNg);
  }

  /** 保留中の再登録スケジュールをキャンセルする */
  private cancelPendingReRegisterSchedule(): void {
    if (this.regRetryTimer !== null) {
      clearTimeout(this.regRetryTimer);
      this.regRetryTimer = null;
    }
    this.isTimeoutScheduled = false;
  }

  /**
   * UA を破棄 → delaySec 後に新規 UA で再登録する。
   * iOS: recreateAccountToResetCallID() 相当（403/404/5xx 対応）。
   */
  private scheduleReregisterWithRecreation(delaySec: number): void {
    if (this.isRetryCall) return;
    this.isRetryCall = true;

    this.cancelPendingReRegisterSchedule();

    // UA を破棄してから delaySec 後に再生成
    this.registerer = null;
    this.userAgent?.stop().catch(() => {});
    this.userAgent = null;

    this.regRetryTimer = setTimeout(() => {
      this.regRetryTimer = null;
      this.isRetryCall = false;
      if (this.callInfo && !this.isStopping) {
        this.initUserAgent(this.callInfo);
      }
    }, Math.max(0, delaySec) * 1000);
  }

  // ============================================================
  // Private: Call handling
  // ============================================================

  /**
   * INVITE 拒否応答に応じたステータス通知。
   * iOS onCallStateChanged case .Error + RegistrationState 相当。
   */
  private handleInviteRejection(code: number): void {
    if (code === 486 || code === 600) {
      // 486 Busy Here / 600 Busy Everywhere
      this.notifyStatus(CallLibraryStatus.busy);
    } else if (code === 404 || code === 480) {
      // 404 Not Found / 480 Temporarily Unavailable
      this.notifyStatus(CallLibraryStatus.notFound);
    } else {
      // その他の失敗 (圏外・通話中など)
      this.notifyStatus(CallLibraryStatus.callFail);
    }
    // Terminated ステートが続いて handleCallEndedIfNeeded → .disconnect が届く
  }

  /**
   * SessionState 変化ハンドラ。
   * iOS onCallStateChanged case .Connected / .Released / .End / .Error 相当。
   */
  private handleSessionStateChange(state: SessionState): void {
    switch (state) {
      case SessionState.Establishing:
        // OutgoingProgress / Ringing: UI 更新は Presenter 側で行う
        break;
      case SessionState.Established:
        // iOS case .Connected → .connect 通知
        this.isCalling = true;
        this.attachRemoteAudio();
        this.notifyStatus(CallLibraryStatus.connect);
        break;
      case SessionState.Terminating:
        break;
      case SessionState.Terminated:
        // iOS case .Released / .End → handleCallEndedIfNeeded
        this.handleCallEndedIfNeeded();
        break;
    }
  }

  /**
   * 新規通話開始前にガードフラグをリセット。
   * iOS: resetCallEndGuardsForNewCall() 相当。
   */
  private resetCallEndGuardsForNewCall(): void {
    this.didNotifyCallDisconnect = false;
    if (this.callEndWatchdogTimer !== null) {
      clearTimeout(this.callEndWatchdogTimer);
      this.callEndWatchdogTimer = null;
    }
    this.activeSessionId = null;
    this.cleanupRemoteAudio();
  }

  /**
   * 通話確立時にリモート音声ストリームを <audio> 要素にアタッチして再生する。
   * SIP.js は WebRTC のリモートトラックを自動再生しないため、
   * SessionDescriptionHandler の remoteMediaStream を明示的に再生する必要がある。
   */
  private attachRemoteAudio(): void {
    const session = this.activeSession;
    if (!session) {
      console.info("[SipManager] attachRemoteAudio: no active session");
      return;
    }

    const sdh = session.sessionDescriptionHandler as unknown as
      | {
          peerConnection?: RTCPeerConnection;
          remoteMediaStream?: MediaStream;
        }
      | undefined;

    if (!sdh) {
      console.info("[SipManager] attachRemoteAudio: no sessionDescriptionHandler");
      return;
    }

    // SIP.js 0.21 の remoteMediaStream ゲッターを優先利用
    const remoteStream =
      sdh.remoteMediaStream ??
      (() => {
        const pc = sdh.peerConnection;
        if (!pc) return null;
        const stream = new MediaStream();
        pc.getReceivers().forEach((receiver) => {
          if (receiver.track) stream.addTrack(receiver.track);
        });
        return stream.getTracks().length > 0 ? stream : null;
      })();

    if (!remoteStream || remoteStream.getTracks().length === 0) {
      console.info("[SipManager] attachRemoteAudio: no remote tracks available");
      return;
    }

    this.cleanupRemoteAudio();
    const audio = new Audio();
    audio.srcObject = remoteStream;
    audio.play().catch((e) => {
      console.error("[SipManager] remoteAudio.play() failed:", e);
    });
    this.remoteAudioElement = audio;
  }

  /** リモート音声再生を停止・クリーンアップする */
  private cleanupRemoteAudio(): void {
    if (this.remoteAudioElement) {
      this.remoteAudioElement.pause();
      this.remoteAudioElement.srcObject = null;
      this.remoteAudioElement = null;
    }
  }

  /**
   * .disconnect を1回だけ通知する。
   * iOS: handleCallEndedIfNeeded() 相当。
   * どんな終わり方（Released/End/Error/terminate失敗）でも必ず1回届く。
   */
  private handleCallEndedIfNeeded(): void {
    // アクティブセッションをクリア（遅延イベント混入防止）
    this.activeSessionId = null;

    if (this.didNotifyCallDisconnect) return;
    this.didNotifyCallDisconnect = true;

    if (this.callEndWatchdogTimer !== null) {
      clearTimeout(this.callEndWatchdogTimer);
      this.callEndWatchdogTimer = null;
    }

    this.isCalling = false;
    this.activeSession = null;
    this.cleanupRemoteAudio();

    this.notifyStatus(CallLibraryStatus.disconnect);
  }

  /**
   * terminate() したのに Terminated が来ない場合の保険タイマー。
   * iOS: scheduleCallEndWatchdog() 相当。
   */
  private scheduleCallEndWatchdog(seconds: number): void {
    if (this.callEndWatchdogTimer !== null) {
      clearTimeout(this.callEndWatchdogTimer);
    }
    this.callEndWatchdogTimer = setTimeout(() => {
      this.callEndWatchdogTimer = null;
      this.handleCallEndedIfNeeded();
    }, seconds * 1000);
  }

  private notifyStatus(status: CallLibraryStatus): void {
    this.onStatusChange?.(status);
  }

  /**
   * ICE サーバー設定を構築する。
   * TURN サーバーが設定されていればリレー候補を含める。
   */
  private buildIceServers(): RTCIceServer[] {
    const servers: RTCIceServer[] = [];
    if (this.callInfo?.turnServerUrl) {
      servers.push({
        urls: this.callInfo.turnServerUrl,
        username: this.callInfo.turnUsername ?? "",
        credential: this.callInfo.turnPassword ?? "",
      });
    }
    return servers;
  }
}

// ============================================================
// SDP Helpers (OfficeLink 互換)
// ============================================================

/**
 * SDP の audio セクションに a=ptime を挿入または上書きするモディファイア。
 * iOS: params.addCustomSdpMediaAttribute(type: .Audio, attributeName: "ptime") 相当。
 */
function createPtimeModifier(ptimeMs: number) {
  return (description: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> => {
    const sdp = description.sdp;
    if (!sdp) return Promise.resolve(description);
    return Promise.resolve({
      ...description,
      sdp: injectPtimeIntoSdp(sdp, ptimeMs),
    });
  };
}

/**
 * SDP の audio セクションに a=ptime 行を追加する。
 * 既に存在する場合は上書き。
 */
function injectPtimeIntoSdp(sdp: string, ptimeMs: number): string {
  const clampedPtime = Math.max(10, Math.min(120, ptimeMs));

  // 既存の a=ptime があれば置換
  if (/a=ptime:\d+/.test(sdp)) {
    return sdp.replace(/a=ptime:\d+/g, `a=ptime:${clampedPtime}`);
  }

  // audio セクションの末尾（次の m= の直前、または文字列末尾）に挿入
  const lines = sdp.split("\r\n");
  const result: string[] = [];
  let inAudio = false;
  let inserted = false;

  for (const line of lines) {
    if (line.startsWith("m=audio")) {
      inAudio = true;
    } else if (line.startsWith("m=") && inAudio && !inserted) {
      result.push(`a=ptime:${clampedPtime}`);
      inserted = true;
      inAudio = false;
    }
    result.push(line);
  }

  if (inAudio && !inserted) {
    // split("\r\n") すると末尾の \r\n が空文字列になるため、
    // その手前に挿入しないと SDP が無効になる
    let insertIdx = result.length;
    while (insertIdx > 0 && result[insertIdx - 1] === "") {
      insertIdx--;
    }
    result.splice(insertIdx, 0, `a=ptime:${clampedPtime}`);
  }

  return result.join("\r\n");
}

// ============================================================
// Export singleton
// ============================================================

/** SipManager シングルトンインスタンス */
export const sipManager = new SipManager();
